import React, { useEffect, useState } from "react";
import axios from "axios";
import { Base_URL } from "../url";
import { useAuth } from "../Context/AuthContext";
import { getBankNames, getAccountNumbers } from "../Api/BankStatements";

const ViewBankStatement = () => {
  const { user } = useAuth();

  const [bankNames, setBankNames] = useState([]);
  const [accountNumbers, setAccountNumbers] = useState([]);
  const [formData, setFormData] = useState({
    bankName: "",
    accountNumber: "",
    year: "",
    month: "",
  });

  const [statements, setStatements] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(20); // fixed 20 in backend
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const organisationId = user?.organisationId || user?.organiastionId;
  const userId = user?.userId || user?.id;

  // 🔹 Build lists for months & years
  const monthOptions = [
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let y = currentYear; y >= currentYear - 5; y--) {
    yearOptions.push({ value: String(y), label: String(y) });
  }

  // 🔹 Load bank names
  useEffect(() => {
    const fetchBankNames = async () => {
      if (!organisationId) return;

      try {
        setError("");
        const response = await getBankNames(organisationId);
        setBankNames(response || []);
      } catch (err) {
        console.error("Error fetching bank names:", err);
        setError("Failed to load bank names.");
      }
    };

    fetchBankNames();
  }, [organisationId]);

  // 🔹 Handle generic change for year/month
  const handleSimpleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setStatements([]);
    setPage(1);
    setTotal(0);
  };

  // 🔹 Handle bank change
  const handleBankChange = async (e) => {
    const selectedBank = e.target.value;

    setFormData((prev) => ({
      ...prev,
      bankName: selectedBank,
      accountNumber: "",
    }));
    setAccountNumbers([]);
    setStatements([]);
    setPage(1);
    setTotal(0);

    if (!selectedBank || !organisationId || !userId) return;

    try {
      setError("");
      const accounts = await getAccountNumbers(selectedBank, userId, organisationId);
      setAccountNumbers(accounts || []);
    } catch (err) {
      console.error("Error fetching account numbers:", err);
      setError("Failed to load account numbers.");
    }
  };

  // 🔹 Handle account change
  const handleAccountChange = (e) => {
    const selectedAccount = e.target.value;

    setFormData((prev) => ({
      ...prev,
      accountNumber: selectedAccount,
    }));
    setStatements([]);
    setPage(1);
    setTotal(0);
  };

  // 🔹 Fetch statements using new route: /{id}/{account}/{page}/{year}/{month}
  const fetchStatements = async (pageNumber = 1) => {
    const { accountNumber, year, month } = formData;

    if (!organisationId || !accountNumber || !year || !month) {
      setError("Please select bank, account, year and month.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const url = `${Base_URL}/BankStatementsData/GetBankStatemntsByOrg/${organisationId}/${accountNumber}/${pageNumber}/${year}/${month}`;
      const response = await axios.get(url);

      const { data, total, pageSize } = response.data || {};

      setStatements(data || []);
      setTotal(total || 0);
      setPageSize(pageSize || 20);
      setPage(pageNumber);
    } catch (err) {
      console.error("Error fetching bank statements:", err);
      setError("Failed to load bank statements.");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Submit = fetch page 1
  const handleSubmit = (e) => {
    e.preventDefault();
    fetchStatements(1);
  };

  // 🔹 Pagination
  const totalPages = total && pageSize ? Math.ceil(total / pageSize) : 0;

  const handlePageChange = (newPage) => {
    if (newPage < 1 || (totalPages && newPage > totalPages)) return;
    fetchStatements(newPage);
  };

  const isFormValid =
    formData.bankName &&
    formData.accountNumber &&
    formData.year &&
    formData.month;

  return (
    <div style={{ padding: "1rem" }}>
      <h3>View Bank Statement</h3>

      {error && (
        <div
          style={{
            marginBottom: "1rem",
            padding: "0.75rem",
            borderRadius: "4px",
            backgroundColor: "#f8d7da",
            color: "#721c24",
          }}
        >
          {error}
        </div>
      )}

      {/* Filter Form */}
      <form onSubmit={handleSubmit} style={{ marginBottom: "1rem" }}>
        <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
          {/* Bank */}
          <div>
            <label style={{ display: "block", marginBottom: "0.25rem" }}>
              Bank Name
            </label>
            <select
              value={formData.bankName}
              onChange={handleBankChange}
              name="bankName"
              required
              style={{ minWidth: "200px", padding: "0.35rem" }}
            >
              <option value="">Select Bank</option>
              {bankNames.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Account */}
          <div>
            <label style={{ display: "block", marginBottom: "0.25rem" }}>
              Account Number
            </label>
            <select
              value={formData.accountNumber}
              onChange={handleAccountChange}
              name="accountNumber"
              required
              disabled={!formData.bankName}
              style={{ minWidth: "200px", padding: "0.35rem" }}
            >
              <option value="">Select Account</option>
              {accountNumbers.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Year */}
          <div>
            <label style={{ display: "block", marginBottom: "0.25rem" }}>
              Year
            </label>
            <select
              name="year"
              value={formData.year}
              onChange={handleSimpleChange}
              required
              style={{ minWidth: "120px", padding: "0.35rem" }}
            >
              <option value="">Select Year</option>
              {yearOptions.map((y) => (
                <option key={y.value} value={y.value}>
                  {y.label}
                </option>
              ))}
            </select>
          </div>

          {/* Month */}
          <div>
            <label style={{ display: "block", marginBottom: "0.25rem" }}>
              Month
            </label>
            <select
              name="month"
              value={formData.month}
              onChange={handleSimpleChange}
              required
              style={{ minWidth: "140px", padding: "0.35rem" }}
            >
              <option value="">Select Month</option>
              {monthOptions.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>

          {/* Submit */}
          <div style={{ alignSelf: "flex-end" }}>
            <button
              type="submit"
              disabled={!isFormValid || loading}
              style={{
                padding: "0.45rem 1rem",
                cursor:
                  !isFormValid || loading ? "not-allowed" : "pointer",
              }}
            >
              {loading ? "Loading..." : "Get Statements"}
            </button>
          </div>
        </div>
      </form>

      {/* Table */}
      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            marginBottom: "1rem",
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  borderBottom: "1px solid #ddd",
                  textAlign: "left",
                  padding: "0.5rem",
                }}
              >
                Date
              </th>
              <th
                style={{
                  borderBottom: "1px solid #ddd",
                  textAlign: "left",
                  padding: "0.5rem",
                }}
              >
                Narration
              </th>
              <th
                style={{
                  borderBottom: "1px solid #ddd",
                  textAlign: "right",
                  padding: "0.5rem",
                }}
              >
                Transaction Amount
              </th>
              <th
                style={{
                  borderBottom: "1px solid #ddd",
                  textAlign: "right",
                  padding: "0.5rem",
                }}
              >
                Available Balance
              </th>
            </tr>
          </thead>
          <tbody>
            {statements && statements.length > 0 ? (
              statements.map((item, index) => (
                <tr key={index}>
                  <td
                    style={{
                      borderBottom: "1px solid #f0f0f0",
                      padding: "0.5rem",
                    }}
                  >
                    {item.valueDate
                      ? new Date(item.valueDate).toLocaleDateString()
                      : ""}
                  </td>
                  <td
                    style={{
                      borderBottom: "1px solid #f0f0f0",
                      padding: "0.5rem",
                      maxWidth: "500px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={item.narration}
                  >
                    {item.narration}
                  </td>
                  <td
                    style={{
                      borderBottom: "1px solid #f0f0f0",
                      padding: "0.5rem",
                      textAlign: "right",
                    }}
                  >
                    {item.transactionAmount}
                  </td>
                  <td
                    style={{
                      borderBottom: "1px solid #f0f0f0",
                      padding: "0.5rem",
                      textAlign: "right",
                    }}
                  >
                    {item.availableBalance}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={4}
                  style={{
                    padding: "0.75rem",
                    textAlign: "center",
                    fontStyle: "italic",
                  }}
                >
                  {loading
                    ? "Loading statements..."
                    : "No statements to display."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 0 && (
        <div
          style={{
            display: "flex",
            gap: "0.75rem",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          <button
            type="button"
            onClick={() => handlePageChange(page - 1)}
            disabled={page <= 1 || loading}
          >
            Previous
          </button>
          <span>
            Page {page} of {totalPages} ({total} records)
          </span>
          <button
            type="button"
            onClick={() => handlePageChange(page + 1)}
            disabled={page >= totalPages || loading}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default ViewBankStatement;
